import React, { useEffect, useImperativeHandle, forwardRef, useMemo } from 'react'
import { parseCrontabToTable, getNextExecution, buildCrontab } from '../../../crontab/Crontab.js'
import { Checkbox, FormControlLabel, TextField, Typography, Grid, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material'
import { CheckCorrectTimeFormat } from '../../../generic/DateTimeFunctions.jsx'
import WarningIcon from '@mui/icons-material/Warning'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation/index.js'
import AlertUI from '../../../generic/AlertUI.jsx'
import { CalendarEventMenu } from '../../calendars/CalendarEventMenu.jsx'
import { rruleObjectToString, rruleStringToObject } from '../../calendars/CalendarRepeatComponent.jsx'

const NumericTextField = ({ value, min, max, onChange }) => {
  const onChangeValue = event => {
    const inputValue = event.target.value
    if (inputValue === '' || inputValue === null || inputValue === undefined || inputValue === ' ') {
      return onChange('')
    }
    if (!/^\d*$/.test(inputValue)) {
      return
    }
    // parse int the input value and check if the value is between the min and max values
    const numberValue = parseInt(inputValue)
    if (numberValue < min || numberValue > max) {
      return
    }
    // Value is correct, so we must trigger onChange
    if (typeof onChange === 'function') {
      onChange(numberValue)
    }
  }

  return (
    <TextField
      value={value ?? ''}
      onChange={onChangeValue}
    />
  )
}

const EventTable = forwardRef(({ crontab, startDate, type = 'WAIT', onChange }, ref) => {
  const [AlertElement, showAlert, dismissAlert] = AlertUI()
  const { t_ } = useSmartTranslation()
  const [tableData, setTableData] = React.useState([])
  const [timeData, setTimeData] = React.useState('')
  const [nextEvent, setNextEvent] = React.useState(null)
  const [checkTime, setCheckTime] = React.useState(false)
  const [showErrors, setShowErrors] = React.useState(false)

  const crontabFieldList = useMemo(() => [
    { id: 'minutes', title: t_('Minutes') + ' (0-59)', min: 0, max: 59 },
    { id: 'hours', title: t_('Hours') + ' (0-23)', min: 0, max: 23 },
    { id: 'dayMonths', title: t_('Day of Month') + ' (1-31)', min: 1, max: 31 },
    { id: 'months', title: t_('Months') + ' (1-12)', min: 1, max: 12 },
    { id: 'dayWeeks', title: t_('Day of Week') + ' (1-7)', min: 1, max: 7 }
  ], [t_])

  /**
   * On crontab change, parse the crontab and set the table data
   */
  useEffect(() => {
    if (!crontab || typeof crontab !== 'string') return
    if (type === 'WAIT') {
      setTimeData(crontab)
    } else if (type === 'PERIODIC') {
      if (isRRule(crontab)) {
        setToggleIndex('cyclic')
        const rruleObj = rruleStringToObject(crontab)
        const dateTime = new Date(startDate)

        setCalendarEvent(prev => ({
          ...prev,
          rrule: rruleObj,
          startDate: dateTime
        }))
      } else {
        setToggleIndex('programmed')
        const result = parseCrontabToTable(crontab)
        setTableData(result)
      }
    }
  }, [crontab, type, startDate])

  useEffect(() => {
    try {
      if (!tableData) return

      let validData = true
      for (const field of crontabFieldList) {
        const value = tableData[field.id]?.value
        if (value === null || isNaN(value)) {
          validData = false
          break
        }
        const numberValue = parseInt(value)
        if (numberValue < field.min || numberValue > field.max) {
          validData = false
          break
        }
      }

      if (!validData) {
        setNextEvent(null)
        return
      }

      const result = buildCrontab(tableData)
      const next = getNextExecution(result)
      setNextEvent(next ? next.toLocaleString() : null)
    } catch (e) {
      showAlert({ severity: 'warning', title: t_(e.message) })
    }
  }, [tableData, crontabFieldList, t_, showAlert])

  const isRRule = (str) => {
    return typeof str === 'string' && str.includes('FREQ=')
  }

  const showEmptyDataAlert = () => {
    showAlert({ severity: 'error', title: t_('Data is required') })
    setTimeout(() => dismissAlert(), 4000)
    return false
  }

  /**
   * Get table data and build crontab to save
   */
  const saveCrontab = () => {
    try {
      if (type === 'WAIT') {
        if (!timeData || timeData === '' || timeData === 0) {
          return showEmptyDataAlert()
        }
        const checkTime = CheckCorrectTimeFormat(timeData)
        if (!checkTime) {
          onChange(timeData)
          return true
        }
      } else if (type === 'PERIODIC') {
        // Check if programmed or cyclic
        if (toggleIndex === 'programmed') {
          const isEmpty = crontabFieldList.every(field => !tableData[field.id]?.value || tableData[field.id]?.value === '')
          if (isEmpty) {
            return showEmptyDataAlert()
          }
          for (const field of crontabFieldList) {
            verifyValue(field.id, tableData[field.id]?.value, true)
          }
          const newCrontab = buildCrontab(tableData)
          onChange(newCrontab)
          return true
        } else if (toggleIndex === 'cyclic') {
          if (calendarEvent.rrule?.BYDAY.length === 0 || calendarEvent.rrule?.INTERVAL === '0') {
            setShowErrors(true)
            return false
          }
          const rruleString = rruleObjectToString(calendarEvent.rrule)
          startDate = calendarEvent.startDate
          onChange(rruleString, startDate)
          if (showAlert) {
            setShowErrors(false)
          }
          return true
        }
      }
    } catch (e) {
      showAlert({ severity: 'warning', title: t_(e.message) })
      return false
    }
  }

  useImperativeHandle(ref, () => ({
    onSave: () => saveCrontab(),
    resetTable: () => resetTableData()
  }))

  /**
   * Method to verify if the value is a number and if it is between the min and max values
   * @param {*} field Field to verify
   * @param {*} value Value to verify
   * @returns Error if the value is not a number or if it is not between the min and max values
   */
  const verifyValue = (field, value) => {
    if (!value) return
    if (!/^\d*$/.test(value)) {
      throw new Error(t_('Value must be a valid number'))
    }
    const numberValue = parseInt(value)
    const fieldData = crontabFieldList.find(f => f.id === field)
    if (numberValue < fieldData.min || numberValue > fieldData.max) {
      throw new Error(t_('Value must be between values') + ':' + fieldData.min + '-' + fieldData.max)
    }
  }

  /**
   * Verify, set and save(crontab format) the new value for the field
   * @param {*} field Field to change
   * @param {*} value New value
   */
  const onChangeValue = (field, value, repeat) => {
    try {
      if (repeat && (value === null || value === '' || value === undefined || isNaN(value))) {
        value = 1
      }

      setTableData(prevData => {
        const newData = {
          ...prevData,
          [field]: { ...prevData?.[field], value, repeat }
        }
        return newData
      })
    } catch (e) {
      showAlert({ severity: 'warning', title: t_(e.message) })
    }
  }

  const handleChangeTimeData = (newValue) => {
    const checkTime = CheckCorrectTimeFormat(newValue)
    setCheckTime(checkTime)
    setTimeData(newValue)
  }

  const resetTableData = () => {
    if (type === 'PERIODIC' && toggleIndex === 'cyclic') {
      setCalendarEvent(defaultCalendarEvent)
      onChange(null, null, null)
      setShowErrors(false)
    } else if (type === 'PERIODIC' && toggleIndex === 'programmed') {
      const newData = {}
      crontabFieldList.forEach(field => {
        newData[field.id] = { value: null, repeat: false }
      })
      setTableData(newData)
    } else if (type === 'WAIT') {
      setTimeData('')
    }
  }

  const [toggleIndex, setToggleIndex] = React.useState('programmed')

  const defaultCalendarEvent = {
    name: '',
    startDate: null,
    endDate: null,
    working: false,
    rrule: rruleStringToObject(null, null),
    systemDefault: false
  }

  const [calendarEvent, setCalendarEvent] = React.useState(defaultCalendarEvent)

  const handleToggleChange = (event, newValue) => {
    if (newValue !== null) {
      setToggleIndex(newValue)
    }
  }

  const handleUpdateCalendarEvent = (event) => {
    setCalendarEvent({
      ...event,
      startDate: event.startDate instanceof Date ? event.startDate : new Date(event.startDate)
    })
  }

  return (
    <>
      {AlertElement}
      {type === 'WAIT'
        ? (
          <div style={{ display: 'flex', flexDirection: 'row', marginTop: '1rem' }}>
            <div style={{ display: 'flex', padding: '1rem', flex: 6, aligItems: 'center', justifyContent: 'center' }}>
              <Typography>{t_('Wait for event')}:</Typography>
            </div>
            <div style={{ flex: 6 }}>
              <TextField
                value={timeData}
                placeholder='hh:mm:ss'
                variant='outlined'
                onChange={(event) => handleChangeTimeData(event.target.value)}
                error={checkTime}
                label={checkTime
                  ? (
                    <Tooltip placement='top' title={checkTime}>
                      <WarningIcon />
                    </Tooltip>
                    )
                  : ''}
              />
            </div>
          </div>
          )
        : (
          <>
            <Grid
              container direction='column'
              justifyContent='center'
              spacing={2}
            >
              <Grid item>
                <ToggleButtonGroup
                  value={toggleIndex}
                  aria-label='Event type'
                  sx={{ bgcolor: 'background.paper' }}
                  onChange={handleToggleChange}
                  exclusive
                >
                  <ToggleButton value='programmed' color='primary'>
                    {t_('Programmed')}
                  </ToggleButton>
                  <ToggleButton value='cyclic' color='primary'>
                    {t_('Cyclic')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>

            {toggleIndex === 'cyclic'
              ? (
                <div style={{ display: 'flex', flexDirection: 'row', marginTop: '1rem' }}>
                  <CalendarEventMenu
                    calendarEvent={calendarEvent}
                    onCalendarEventChange={handleUpdateCalendarEvent}
                    showErrors={showErrors}
                    isJobLinkerEvent
                  />
                </div>
                )
              : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', paddingTop: '1.5rem', fontWeight: 'bold' }}>
                    <div style={{ flex: 3 }} />
                    <div style={{ flex: 1 }}>{t_('Repeat')}</div>
                  </div>
                  {crontabFieldList.map((field) => (
                    <div key={field.id} style={{ display: 'flex', textAlign: 'start', padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', flex: 6 }}>
                        <Typography>{field.title}:</Typography>
                      </div>
                      <div style={{ flex: 3 }}>
                        <NumericTextField
                          value={tableData?.[field.id]?.value ?? ''}
                          min={field.min}
                          max={field.max}
                          onChange={(newValue) => onChangeValue(field.id, newValue, tableData?.[field.id]?.repeat)}
                        />
                      </div>
                      <div style={{ flex: 3 }} />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={tableData?.[field.id]?.repeat ?? false}
                            onChange={(event) => { onChangeValue(field.id, tableData?.[field.id]?.value, event.target.checked) }}
                          />
                        }
                      />
                    </div>
                  ))}
                  {nextEvent && (
                    <Typography sx={{ marginTop: '1rem' }}>{t_('Next at')}: {nextEvent}</Typography>
                  )}
                </div>
                )}
          </>
          )}
    </>
  )
})

export default EventTable
